import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '../../utils/supabase'
import type { NewNotificationInput, Notification } from '../../types/notifications'
import { NOTIFICATION_DEFAULT_KIND } from '../../constants/notifications'
import type { NotificationListener, NotificationSource } from './NotificationSource'
import {
  NotificationApiClient,
  rowToNotification,
  type NotificationApiRow,
} from './notificationApiClient'

/**
 * Backend-backed NotificationSource.
 *
 * Lifecycle:
 *   - Constructed once at module load. Subscribes to Supabase auth changes.
 *   - When a user is present: fetch the inbox via REST, then open a Realtime
 *     subscription on the `notifications` table filtered to that user_id so
 *     INSERT / UPDATE / DELETE events push live into the local cache.
 *   - On sign-out: clear cache and tear down the Realtime channel.
 *
 * Local-only behavior:
 *   - `add()` inserts into the local cache only. It is the escape hatch for
 *     transient client-side notifications (e.g. "Saved") that don't need to
 *     persist server-side. Persistent notifications are created by the backend
 *     (see NotificationService.create on the server) and arrive via Realtime.
 */
export class ApiNotificationSource implements NotificationSource {
  private items: Notification[] = []
  private listeners = new Set<NotificationListener>()
  private channel: RealtimeChannel | null = null
  private currentUserId: string | null = null

  constructor() {
    this.bootstrapFromCurrentSession()
    supabase.auth.onAuthStateChange((_event, session) => {
      const nextUserId = session?.user.id ?? null
      if (nextUserId === this.currentUserId) return
      this.teardown()
      if (nextUserId) {
        this.setupForUser(nextUserId)
      } else {
        this.replaceItems([])
      }
    })
  }

  getAll(): Notification[] {
    return this.items
  }

  subscribe(listener: NotificationListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  add(input: NewNotificationInput): Notification {
    const created: Notification = {
      id: globalThis.crypto.randomUUID(),
      kind: input.kind ?? NOTIFICATION_DEFAULT_KIND,
      title: input.title,
      body: input.body,
      link: input.link,
      data: input.data,
      createdAt: new Date().toISOString(),
      readAt: null,
    }
    this.replaceItems([created, ...this.items])
    return created
  }

  markRead(id: string): void {
    const target = this.items.find((n) => n.id === id)
    if (!target || target.readAt !== null) return
    const now = new Date().toISOString()
    this.replaceItems(this.items.map((n) => (n.id === id ? { ...n, readAt: now } : n)))
    void NotificationApiClient.markRead(id).catch(() => {
      // Network/404 (e.g. a local-only notification): keep the optimistic state.
    })
  }

  markAllRead(): void {
    if (!this.items.some((n) => n.readAt === null)) return
    const now = new Date().toISOString()
    this.replaceItems(this.items.map((n) => (n.readAt === null ? { ...n, readAt: now } : n)))
    void NotificationApiClient.markAllRead().catch(() => {
      // Best-effort: any backend failure leaves the optimistic state in place.
    })
  }

  remove(id: string): void {
    const next = this.items.filter((n) => n.id !== id)
    if (next.length === this.items.length) return
    this.replaceItems(next)
    void NotificationApiClient.remove(id).catch(() => {
      // Optimistic; local-only items will 404 server-side, which is fine.
    })
  }

  clear(): void {
    if (this.items.length === 0) return
    this.replaceItems([])
    void NotificationApiClient.clear().catch(() => {
      // Optimistic clear stands even if the server call fails.
    })
  }

  private bootstrapFromCurrentSession(): void {
    void supabase.auth.getSession().then(({ data }) => {
      const userId = data.session?.user.id ?? null
      if (!userId || userId === this.currentUserId) return
      this.setupForUser(userId)
    })
  }

  private setupForUser(userId: string): void {
    this.currentUserId = userId

    void NotificationApiClient.list()
      .then((list) => {
        if (this.currentUserId !== userId) return
        this.replaceItems(list)
      })
      .catch(() => {
        // Initial fetch failure leaves an empty list; Realtime can still
        // populate it as new notifications arrive.
      })

    this.channel = supabase
      .channel(`notifications:user:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const next = rowToNotification(payload.new as NotificationApiRow)
          this.upsert(next)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const next = rowToNotification(payload.new as NotificationApiRow)
          this.upsert(next)
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const oldRow = payload.old as Partial<NotificationApiRow>
          if (oldRow.id) this.removeById(oldRow.id)
        }
      )
      .subscribe()
  }

  private teardown(): void {
    if (this.channel) {
      void supabase.removeChannel(this.channel)
      this.channel = null
    }
    this.currentUserId = null
  }

  private upsert(notification: Notification): void {
    const existingIndex = this.items.findIndex((n) => n.id === notification.id)
    if (existingIndex === -1) {
      this.replaceItems([notification, ...this.items])
      return
    }
    const next = [...this.items]
    next[existingIndex] = notification
    this.replaceItems(next)
  }

  private removeById(id: string): void {
    const next = this.items.filter((n) => n.id !== id)
    if (next.length !== this.items.length) {
      this.replaceItems(next)
    }
  }

  private replaceItems(next: Notification[]): void {
    this.items = next
    this.emit()
  }

  private emit(): void {
    const snapshot = this.items
    this.listeners.forEach((listener) => listener(snapshot))
  }
}
